import {useRouteError, isRouteErrorResponse, Link} from 'react-router-dom';
import {Button} from '@/matic/components/ui/button';

/**
 * Error boundary for routes
 * Catches errors thrown during rendering, data loading, or in event handlers
 */
export function ErrorBoundary() {
  const error = useRouteError();

  // Type guard for route errors
  if (isRouteErrorResponse(error)) {
    // Router-specific errors (404, etc.)
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md space-y-4 p-6 text-center">
          <h1 className="text-4xl font-bold text-gray-900">{error.status}</h1>
          <p className="text-lg text-gray-600">{error.statusText}</p>
          {error.data?.message && (
            <p className="text-sm text-gray-500">{error.data.message}</p>
          )}
          <Link to="/documents">
            <Button variant="primary">Go to Documents</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Generic error
  const errorMessage = error instanceof Error ? error.message : 'Unknown error';
  const errorStack = error instanceof Error ? error.stack : undefined;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="max-w-2xl space-y-4 p-6">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-bold text-red-600">
            Something went wrong
          </h1>
          <p className="text-gray-600">An unexpected error occurred</p>
        </div>

        <div className="rounded-lg bg-red-50 p-4">
          <p className="font-mono text-sm text-red-800">{errorMessage}</p>
        </div>

        {import.meta.env.DEV && errorStack && (
          <details className="rounded-lg bg-gray-100 p-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              Stack trace (dev only)
            </summary>
            <pre className="mt-2 overflow-auto text-xs text-gray-600">
              {errorStack}
            </pre>
          </details>
        )}

        <div className="flex justify-center gap-4">
          <Link to="/documents">
            <Button variant="primary">Go to Documents</Button>
          </Link>
          <Button
            variant="outline"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            Reload Page
          </Button>
        </div>
      </div>
    </div>
  );
}
